import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuotes } from '@/hooks/useQuotes';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Plus, Search } from 'lucide-react';
import { CompanyLookupInput, KVKCompany } from './CompanyLookupInput';
import { useToast } from '@/hooks/use-toast';

interface NewQuoteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const NewQuoteModal = ({ open, onOpenChange }: NewQuoteModalProps) => {
  const navigate = useNavigate();
  const { createQuote } = useQuotes();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [creatingOrg, setCreatingOrg] = useState(false);

  const [organizations, setOrganizations] = useState<Array<{ id: string; name: string }>>([]);
  const [contacts, setContacts] = useState<Array<{ id: string; first_name: string; last_name: string }>>([]);
  const [projects, setProjects] = useState<Array<{ id: string; name: string; project_number: string }>>([]);
  const [templates, setTemplates] = useState<Array<{ id: string; name: string; template_body: string; template_terms: string }>>([]);

  const [formData, setFormData] = useState({
    title: '',
    organization_id: '',
    contact_id: '',
    project_id: '',
    template_id: '',
  });

  const fetchOrganizations = async () => {
    const { data } = await supabase.from('organizations').select('id, name').is('deleted_at', null).order('name');
    if (data) setOrganizations(data);
  };

  useEffect(() => {
    const fetchData = async () => {
      const [orgsResult, projectsResult, templatesResult] = await Promise.all([
        supabase.from('organizations').select('id, name').is('deleted_at', null).order('name'),
        supabase.from('projects').select('id, name, project_number').is('deleted_at', null).order('name'),
        supabase.from('quote_templates').select('id, name, template_body, template_terms').is('deleted_at', null).order('name'),
      ]);

      if (orgsResult.data) setOrganizations(orgsResult.data);
      if (projectsResult.data) setProjects(projectsResult.data);
      if (templatesResult.data) setTemplates(templatesResult.data);
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.organization_id) {
      const fetchContacts = async () => {
        const { data } = await supabase
          .from('contacts')
          .select('id, first_name, last_name')
          .eq('org_id', formData.organization_id)
          .order('first_name');
        if (data) setContacts(data);
      };
      fetchContacts();
    } else {
      setContacts([]);
      setFormData((prev) => ({ ...prev, contact_id: '' }));
    }
  }, [formData.organization_id]);

  const handleQuickCreateOrg = async (company: KVKCompany) => {
    setCreatingOrg(true);
    try {
      const { data, error } = await supabase
        .from('organizations')
        .insert({
          name: company.name,
          kvk: company.kvkNumber,
          status: 'prospect',
          classification: 'klant',
        })
        .select('id, name')
        .single();

      if (error) throw error;

      toast({
        title: 'Organisatie aangemaakt',
        description: `${company.name} is toegevoegd`,
      });

      // Refresh organizations and select the new one
      await fetchOrganizations();
      setFormData(prev => ({ ...prev, organization_id: data.id }));
      setShowQuickCreate(false);
    } catch (error: any) {
      console.error('Error creating organization:', error);
      toast({
        title: 'Fout',
        description: 'Kon organisatie niet aanmaken',
        variant: 'destructive',
      });
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.organization_id) return;

    setLoading(true);
    try {
      const selectedTemplate = templates.find((t) => t.id === formData.template_id);
      const selectedProject = projects.find((p) => p.id === formData.project_id);
      
      const quote = await createQuote({
        title: formData.title,
        organization_id: formData.organization_id,
        contact_id: formData.contact_id || undefined,
        project_id: formData.project_id || undefined,
        template_id: formData.template_id || undefined,
        body: selectedTemplate?.template_body || undefined,
        terms: selectedTemplate?.template_terms || undefined,
        quote_number: selectedProject?.project_number || undefined,
      });

      onOpenChange(false);
      navigate(`/crm/quotes/${quote.id}`);
    } catch (error) {
      console.error('Error creating quote:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Nieuwe offerte aanmaken</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Titel *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Bijv. Implementatie Greenvision CRM"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="organization">Organisatie *</Label>
            <Select
              value={formData.organization_id}
              onValueChange={(value) => setFormData({ ...formData, organization_id: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecteer organisatie" />
              </SelectTrigger>
              <SelectContent>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Quick create organization */}
            {!showQuickCreate ? (
              <Button
                type="button"
                variant="link"
                size="sm"
                className="p-0 h-auto text-xs"
                onClick={() => setShowQuickCreate(true)}
              >
                <Plus className="h-3 w-3 mr-1" />
                Organisatie niet gevonden? Maak direct aan via KVK
              </Button>
            ) : (
              <div className="p-3 border rounded-md bg-muted/50 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Nieuwe organisatie via KVK</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => setShowQuickCreate(false)}
                  >
                    Annuleren
                  </Button>
                </div>
                <CompanyLookupInput
                  onSelect={handleQuickCreateOrg}
                  placeholder="Zoek op bedrijfsnaam of KVK-nummer..."
                  disabled={creatingOrg}
                />
                {creatingOrg && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Organisatie aanmaken...
                  </div>
                )}
              </div>
            )}
          </div>

          {formData.organization_id && contacts.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="contact">Contactpersoon</Label>
              <Select
                value={formData.contact_id}
                onValueChange={(value) => setFormData({ ...formData, contact_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer contactpersoon (optioneel)" />
                </SelectTrigger>
                <SelectContent>
                  {contacts.map((contact) => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {contact.first_name} {contact.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="project">Project</Label>
            <Select
              value={formData.project_id}
              onValueChange={(value) => setFormData({ ...formData, project_id: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecteer project (optioneel)" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.project_number} - {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {templates.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="template">Template</Label>
              <Select
                value={formData.template_id}
                onValueChange={(value) => setFormData({ ...formData, template_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer template (optioneel)" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuleren
            </Button>
            <Button type="submit" disabled={loading || !formData.title || !formData.organization_id}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Offerte aanmaken
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};